import { ChangeEvent, Fragment } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import classNames from 'classnames/bind'

import { Table, Tr, Th, Td, useHoverByClass } from 'app/common/ui-components/table'

import Paging from 'app/common/ui/Paging'
import type { SortHandle, SortState } from 'app/common/containers/withSortStateDouble'
import CSSSticky from 'app/common/ui-components/css-sticky'
import { ReservationHistoryModal } from 'app/common/components-next/reservation-history'
import { FundReturnModal } from 'app/common/components-next/fund-return'
import { useCurrencySign, options as co } from 'app/common/components-next/currency'
import DetailsHeader from 'app/components/common/reports/DetailsHeader'
import { isValueZero } from 'app/common/templates-next/table-template'
import PlainTooltip from 'app/common/ui-next/plain-tooltip'
import { Name } from 'app/common/classes/name'

import enhanceUsersByGroup from './enhanceUsersByGroup'
import type { UserByGroupType } from './selectActual'

import ts from '../Table.scss'
import styles from './UsersByGroup.scss'

const cx = classNames.bind(styles)
const sameRowCN = 'row_'

type Props = {
  handlePage: (e: ChangeEvent<HTMLElement>) => void
  users: Array<Array<UserByGroupType>>
  currentPage: number
  total: number
  rows: number
  sort: SortState
  handleSort1: SortHandle
  handleSort2: SortHandle
  onInvalidateReport: () => void
  isPayerDataVisible?: boolean
}

const UsersByGroup = ({
  handlePage,
  users,
  currentPage,
  total,
  rows,
  sort: { sortKey1, sortKey2, sortDirection1, sortDirection2 },
  handleSort1,
  handleSort2,
  onInvalidateReport,
  isPayerDataVisible,
}: Props) => {
  console.log(users, isPayerDataVisible)
  const current: boolean = useSelector(
    (state: any) => state.reports.balance.params.current,
    shallowEqual,
  )
  const [onMouseEnter, onMouseLeave] = useHoverByClass(sameRowCN)
  const { e_money_enabled: eMoneyEnabled } = useSelector((state: any) => state.dealer, shallowEqual)
  const currency = useCurrencySign(co.COMMA_SPACE_BEFORE)

  return (
    <Fragment>
      <DetailsHeader reportTitle="Остаток средств" />

      {/* ЗАГОЛОВОК */}
      <CSSSticky
        number={2}
        className={styles.stickableTableHeader}
        stickyClassName={styles.tableSticked}
        shadowClassName={styles.shadow}
      >
        <Table className={styles.table}>
          <thead>
            <Tr header>
              {!isPayerDataVisible && (
                <Th
                  id="empty1"
                  className={cx(styles.headerCell, {
                    cellEmpty: !current,
                    cellEmptySmall: current,
                  })}
                >
                  &nbsp;
                </Th>
              )}

              <Th
                id="header1"
                className={cx(styles.headerCell, styles.cellGroup)}
                sortKey="group"
                sortDirection={sortKey1 === 'group' ? sortDirection1 : undefined}
                onSortArrowClick={handleSort1}
                alignLeft
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {'Класс\u00A0/ Должность'}
              </Th>

              <Th
                id="header2"
                className={cx(styles.headerCell, styles.cellName)}
                sortKey="name"
                sortDirection={sortKey2 === 'name' ? sortDirection2 : undefined}
                onSortArrowClick={handleSort2}
                alignLeft
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {'ФИО'}
              </Th>

              <Th
                id="header3"
                className={cx(styles.headerCell, styles.cellBalance)}
                sortKey="balance"
                sortDirection={sortKey2 === 'balance' ? sortDirection2 : undefined}
                onSortArrowClick={handleSort2}
                alignRight
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {`Баланс${currency}`}
              </Th>

              {isPayerDataVisible && (
                <Th
                  id="header5"
                  className={cx(styles.headerCell, styles.cellParents)}
                  colSpan={3}
                  alignCenter
                >
                  {'Родители'}
                </Th>
              )}

              <Th
                id="header4"
                className={cx(styles.headerCell, styles.cellLinks)}
                alignLeft
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {'Дополнительно'}
              </Th>

              {!isPayerDataVisible && (
                <Th
                  id="empty2"
                  className={cx(styles.headerCell, {
                    cellEmpty: !current,
                    cellEmptySmall: current,
                  })}
                >
                  &nbsp;
                </Th>
              )}
            </Tr>
            {isPayerDataVisible && (
              <Tr>
                <Th id="parent_fio" className={styles.cellParentsCol} alignCenter ignoreFirstChild>
                  {'ФИО'}
                </Th>
                <Th id="parent_phone" className={styles.cellParentsCol} alignCenter>
                  {'Телефон'}
                </Th>
                <Th id="parent_email" className={styles.cellParentsCol} alignCenter ignoreLastChild>
                  {'E-mail'}
                </Th>
              </Tr>
            )}
          </thead>
        </Table>
      </CSSSticky>

      <Table className={styles.table}>
        <tbody>
          {users.map((gp, j) => {
            const rowSpan = gp.reduce(
              (acc, { parents }) => (parents ? acc + parents.length : acc),
              0,
            )
            const rowSpanProps = rowSpan > 1 ? { rowSpan } : {}

            return gp.map(({ id, group, name, balance, customerId, parents }, i) => {
              const rowSpanItem =
                isPayerDataVisible && parents && parents.length > 1 ? parents.length : 1
              const rowSpanItemProps = rowSpan > 1 ? { rowSpan: rowSpanItem } : {}

              return (
                <Fragment key={id}>
                  <Tr
                    interlineBorder={i === 0}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className={`${sameRowCN}${j}`}
                    alignTop
                  >
                    {!isPayerDataVisible && (
                      <Td
                        headers="empty1"
                        {...rowSpanProps}
                        className={cx({ cellEmpty: !current, cellEmptySmall: current })}
                      >
                        &nbsp;
                      </Td>
                    )}

                    {i === 0 && (
                      <Td headers="header1" {...rowSpanProps} className={cx(styles.cellGroup)}>
                        {group}
                      </Td>
                    )}

                    {i !== 0 && (
                      <Td headers="header1" {...rowSpanProps} className={cx(styles.cellGroup)}>
                        &nbsp;
                      </Td>
                    )}

                    <Td
                      headers="header2"
                      className={cx(styles.cellName, styles.overridePadding)}
                      {...rowSpanItemProps}
                    >
                      {name}
                    </Td>

                    <Td
                      headers="header3"
                      className={cx(styles.cellBalance, styles.overridePadding, {
                        zero: isValueZero(balance),
                      })}
                      alignRight
                      {...rowSpanItemProps}
                    >
                      {balance}
                    </Td>

                    {isPayerDataVisible && (
                      <>
                        <Td headers="parent_fio" className={styles.cellParentsCol} alignLeft>
                          <PlainTooltip
                            text={parents?.[0]?.name && new Name(parents[0].name).toString()}
                          >
                            {parents?.[0]?.name && new Name(parents[0].name).toString()}
                          </PlainTooltip>
                        </Td>
                        <Td headers="parent_phone" className={styles.cellParentsCol} alignCenter>
                          <PlainTooltip text={parents?.[0]?.phone || '—'}>
                            {parents?.[0]?.phone || '—'}
                          </PlainTooltip>
                        </Td>
                        <Td headers="parent_email" className={styles.cellParentsCol} alignCenter>
                          <PlainTooltip text={parents?.[0]?.email || '—'}>
                            {parents?.[0]?.email || '—'}
                          </PlainTooltip>
                        </Td>
                      </>
                    )}

                    {current && (
                      <Td headers="header4" className={cx(styles.cellLinks)} {...rowSpanItemProps}>
                        <ReservationHistoryModal customerId={customerId} name={name} group={group}>
                          {'Резерв-ние'}
                        </ReservationHistoryModal>{' '}
                        {parseFloat(balance) > 0 && !eMoneyEnabled && (
                          <FundReturnModal
                            customerId={customerId}
                            name={name}
                            group={group}
                            onRefund={onInvalidateReport}
                          >
                            {'Возврат'}
                          </FundReturnModal>
                        )}
                      </Td>
                    )}

                    {!isPayerDataVisible && (
                      <Td
                        headers="empty2"
                        {...rowSpanProps}
                        className={cx({ cellEmpty: !current, cellEmptySmall: current })}
                      >
                        &nbsp;
                      </Td>
                    )}
                  </Tr>

                  {parents &&
                    rowSpan > 1 &&
                    parents.slice(1).map((parent, index) => {
                      const parentName = parent.name && new Name(parent.name).toString()
                      return (
                        <Tr
                          key={parentName || index}
                          className={`${sameRowCN}${j}`}
                          onMouseEnter={onMouseEnter}
                          onMouseLeave={onMouseLeave}
                          alignTop
                        >
                          <Td
                            headers="parent_fio"
                            className={styles.cellParentsCol}
                            alignLeft
                            ignoreFirstChild
                          >
                            <PlainTooltip text={parentName}>{parentName}</PlainTooltip>
                          </Td>
                          <Td headers="parent_phone" className={styles.cellParentsCol} alignCenter>
                            <PlainTooltip text={parent.phone}>{parent.phone || '—'}</PlainTooltip>
                          </Td>
                          <Td
                            headers="parent_email"
                            className={styles.cellParentsCol}
                            alignCenter
                            ignoreLastChild
                          >
                            <PlainTooltip text={parent.email}>{parent.email || '—'}</PlainTooltip>
                          </Td>
                        </Tr>
                      )
                    })}
                </Fragment>
              )
            })
          })}
        </tbody>
      </Table>

      {total > rows && (
        <Paging
          className={ts.pager}
          total={total}
          pageSize={rows}
          currentPage={currentPage}
          currentPageSize={users.length}
          onPage={handlePage}
        />
      )}
    </Fragment>
  )
}

export default enhanceUsersByGroup(UsersByGroup)
